/*
 * Main Product Gallery — Swiper init + variant routing.
 *
 * Mounts on every `.product-main-gallery` element rendered by
 * snippets/product-main-gallery.liquid. The Liquid pre-renders
 * Swiper-shaped markup (.swiper > .swiper-wrapper > .swiper-slide
 * for both the main track and the thumbs strip); this script
 * builds the Swiper instances and wires them together.
 *
 * Settings come in via data-* attributes on the gallery root:
 *   data-layout            'slider' | 'scroll'  (Gallery: Layout)
 *   data-loop              'true'   | 'false'   (Loop Slider)
 *   data-autoplay          'true'   | 'false'   (Autoplay)
 *   data-autoplay-delay    milliseconds         (Autoplay Delay)
 *   data-show-arrows       'true'   | 'false'   (Show Arrows)
 *   data-show-bullets      'true'   | 'false'   (Show Pagination Bullets)
 *   data-show-thumbnails   'true'   | 'false'   (Show Thumbnails, > 1 media)
 *
 * Scroll mode (desktop only): when layout=scroll and viewport
 * ≥ 992px, the main slider stays as a vertical stack — no Swiper
 * is initialised on the main track and arrows / bullets / thumbs
 * are hidden. Mobile always swipes (Swiper still runs).
 *
 * Variant routing: listens for H3.0's `variant:update` event,
 * looks up the slide carrying `data-variant-id="{variant.id}"`,
 * and navigates the main Swiper to it. Slides without a
 * data-variant-id are media not tied to a specific variant and
 * are skipped by the lookup.
 *
 * Swiper is loaded as a UMD bundle in snippets/scripts.liquid,
 * exposing window.Swiper globally. See assets/testimonials.js for
 * the same init pattern.
 */

const Swiper = window.Swiper;

const SELECTOR = '.product-main-gallery';
const INSTANCE_KEY = '_productMainGalleryInstance';
const DESKTOP_QUERY = '(min-width: 992px)';

class ProductMainGallery {
  constructor(root) {
    this.root = root;
    this.layout = root.dataset.layout || 'slider';
    this.loop = root.dataset.loop === 'true';
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
    this.showArrows = root.dataset.showArrows === 'true';
    this.showBullets = root.dataset.showBullets === 'true';
    this.showThumbnails = root.dataset.showThumbnails === 'true';

    this.mainEl = root.querySelector('.product-main-gallery__main');
    this.thumbsEl = root.querySelector('.product-main-gallery__thumbs');
    this.prevEl = root.querySelector('.product-main-gallery__arrow--prev');
    this.nextEl = root.querySelector('.product-main-gallery__arrow--next');
    this.bulletsEl = root.querySelector('.product-main-gallery__bullets');

    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.abortController = new AbortController();

    // Lightbox state -- elements + lazy-init flag. Only ever
    // populated when layout=scroll AND viewport >= 992px; the
    // Liquid template renders the markup conditionally and CSS
    // hides any leftover lightbox node below the breakpoint.
    // `_lightboxPreviousFocus` holds the element that opened
    // the modal so focus can return there on close.
    this.lightboxEl = root.querySelector('.product-main-gallery__lightbox');
    this.lightboxSwiperEl = root.querySelector('.product-main-gallery__lightbox-swiper');
    this.lightboxSwiper = null;
    this._lightboxPreviousFocus = null;

    this.init();
    this._bindLightbox();
  }

  init() {
    if (!this.mainEl) return;

    const slides = this.mainEl.querySelectorAll('.swiper-slide');
    const hasMultiple = slides.length > 1;

    // Single-image gallery: no Swiper needed. Add .is-initialized so
    // the .not-loaded pre-init rules in component-main-product.css
    // stop hiding the slides past the first one (there's only one,
    // and it should render at the column's natural dimensions).
    if (!hasMultiple) {
      this.root.classList.remove('not-loaded');
      this.root.classList.add('is-initialized');
      this._bindVariantUpdate();
      return;
    }

    // Scroll-mode desktop note: the merchant's `layout=scroll` now
    // renders BOTH a grid tree (.product-main-gallery__grid, shown
    // >= 992) AND the slider tree (.product-main-gallery__main,
    // shown < 992). Swiper still initialises here for the tablet /
    // mobile use case; CSS hides the slider tree on desktop scroll
    // mode so it paints nothing. No special JS branching needed.

    // Build thumbs swiper first (so the main can bind to it).
    // centerInsufficientSlides centres the strip horizontally when
    // the thumbs don't fill the available width; once they overflow,
    // freeMode lets the merchant scroll them naturally.
    //
    // observer + observeParents re-measure on container size changes,
    // covering the init-on-zero-width race (slides measured as 0px
    // at init lock the slider in `watchOverflow` mode — see the main
    // Swiper config for the matching guard).
    if (this.showThumbnails && this.thumbsEl) {
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centerInsufficientSlides: true,
        observer: true,
        observeParents: true,
      });
    }

    // watchOverflow: false — Swiper's default is to lock the slider
    // when slide widths sum < container width. At our init point, the
    // gallery's grid column hasn't always settled, so slides measure
    // 0px → Swiper thinks "everything fits" → locks navigation +
    // hides bullets + disables arrows even though there are real
    // multi-image slides. The static / single-slide branch above
    // already handles the "no slider needed" case, so we never need
    // Swiper's overflow auto-hide here.
    // observer + observeParents re-trigger Swiper's measurements when
    // the container resizes (customizer drawer toggles, viewport
    // changes, late-loading images settling layout).
    // Defaults are tablet + mobile: 1.1 slides per view (peek the
    // next slide) with a 4px gap. The 992px breakpoint reverts to
    // single-slide-no-gap for desktop — desktop is grid layout in
    // most layouts anyway, but in `scroll` / single-image modes the
    // slider still runs and should match the design's full-bleed
    // single image.
    const mainOptions = {
      slidesPerView: 1.1,
      spaceBetween: 4,
      loop: this.loop && slides.length > 1,
      speed: 400,
      watchOverflow: false,
      observer: true,
      observeParents: true,
      breakpoints: {
        992: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    };

    if (this.autoplayEnabled) {
      mainOptions.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    if (this.showArrows && this.prevEl && this.nextEl) {
      mainOptions.navigation = {
        prevEl: this.prevEl,
        nextEl: this.nextEl,
      };
    }

    if (this.showBullets && this.bulletsEl) {
      mainOptions.pagination = {
        el: this.bulletsEl,
        clickable: true,
      };
    }

    if (this.thumbsSwiper) {
      mainOptions.thumbs = { swiper: this.thumbsSwiper };
    }

    this.mainSwiper = new Swiper(this.mainEl, mainOptions);

    // Initial active-thumb sync. Swiper's Thumbs module relies on
    // `slidesPerViewDynamic()`, which needs computed slide widths
    // to run — with `slidesPerView: 'auto'` on the thumbs strip,
    // those widths aren't measured yet at init, so `thumbs.update()`
    // silently bails. A manual classList toggle keyed off the main
    // swiper's `realIndex` (loop-safe — `activeIndex` would be off by
    // the loop clones' count) sidesteps the timing dependency. The
    // slideChange binding keeps the marker synced through every
    // navigation including the variant-routed jumps.
    if (this.thumbsSwiper) {
      const sync = () => {
        const i = this.mainSwiper.realIndex;
        this.thumbsEl
          .querySelectorAll('.product-main-gallery__thumb')
          .forEach((t, n) => t.classList.toggle('swiper-slide-thumb-active', n === i));
      };
      sync();
      this.mainSwiper.on('slideChange', sync);
    }

    // Slide counter — current/total readout shown bottom-left of
    // the main gallery on tablet + mobile (CSS-gated). Track
    // `realIndex` rather than `activeIndex` so the loop clones
    // don't throw the counter off; +1 to translate to 1-based
    // for the visible label.
    const counterCurrentEl = this.root.querySelector('.product-main-gallery__counter-current');
    if (counterCurrentEl) {
      const syncCounter = () => {
        counterCurrentEl.textContent = String(this.mainSwiper.realIndex + 1);
      };
      syncCounter();
      this.mainSwiper.on('slideChange', syncCounter);
    }

    this.root.classList.remove('not-loaded');
    this.root.classList.add('is-initialized');

    this._bindVariantUpdate();
    this._bindCustomizerSelect(slides);
  }

  _bindVariantUpdate() {
    // Listen for H3.0's variant:update event and navigate the main
    // slider to the slide tied to the newly-selected variant. Scope
    // by .shopify-section so multiple PDPs on the page don't bleed.
    const section = this.root.closest('.shopify-section');
    const target = section || document;

    target.addEventListener(
      'variant:update',
      (event) => {
        const detail = event && event.detail;
        const resource = detail && detail.resource;
        const variantId = resource && resource.id;
        if (!variantId) return;
        this._goToVariant(String(variantId));
      },
      { signal: this.abortController.signal }
    );
  }

  _goToVariant(variantId) {
    // Scroll-mode desktop: scroll the grid item into view. The grid
    // tree is rendered alongside the slider tree (CSS hides one or
    // the other per breakpoint). Use the grid item when it's the
    // visible tree; fall back to the slider item otherwise.
    const isDesktopScroll =
      this.layout === 'scroll' && window.matchMedia(DESKTOP_QUERY).matches;
    if (isDesktopScroll) {
      const gridItem = this.root.querySelector(
        `.product-main-gallery__grid-item[data-variant-id="${variantId}"]`
      );
      if (gridItem) {
        // Deliberately does NOT scroll. The grid is a vertical column
        // that scrolls with the page, so moving to the variant's image
        // means moving the PAGE — which yanks the reader away from the
        // buy area mid-interaction, and fires on first paint too
        // (product-purchase-form's _syncFromUrl dispatches
        // variant:update so the gallery can pick up ?variant=, so
        // arriving from a product card scrolled the shopper down the
        // page before they had read anything). The variant's image is
        // already in the column; let the reader find it.
        return;
      }
    }

    const slide = this.mainEl.querySelector(
      `.swiper-slide[data-variant-id="${variantId}"]`
    );
    if (!slide) return;

    if (this.mainSwiper) {
      // For loop mode use the data-swiper-slide-index attribute Swiper
      // stamps on each real slide so slideToLoop targets the original
      // index rather than the duplicated clones.
      if (this.mainSwiper.params.loop) {
        const loopIndex = parseInt(slide.dataset.swiperSlideIndex, 10);
        if (!Number.isNaN(loopIndex)) {
          this.mainSwiper.slideToLoop(loopIndex);
          return;
        }
      }
      const slides = Array.from(this.mainEl.querySelectorAll('.swiper-slide'));
      const idx = slides.indexOf(slide);
      if (idx >= 0) this.mainSwiper.slideTo(idx);
    } else {
      // Single-image gallery — nothing to navigate to. The lone slide
      // is the variant's image already, so there is nothing to move to
      // and no reason to move the page.
    }
  }

  _bindCustomizerSelect(slides) {
    // shopify:block:select — merchant clicked a block in the
    // customizer tree. We don't have addressable slides in our schema
    // (media is product data, not blocks), so this is a no-op for
    // now. Wired here so we have a single place to extend if the
    // gallery later exposes slide-like blocks.
  }

  /**
   * Lightbox -- desktop scroll-layout only. Bind click +
   * keyboard activation on every `.product-main-gallery__grid-
   * item[data-lightbox-trigger]`; on activation toggle the
   * modal element's `.is-open` class (the CSS handles the
   * fade) and lazy-init the inner swiper on first open.
   *
   * Short-circuits on every path that doesn't match the
   * scroll-layout + desktop scope (no lightbox in DOM, layout
   * != scroll, viewport < 992px) so the bindings never fire
   * when the lightbox shouldn't.
   */
  _bindLightbox() {
    if (!this.lightboxEl || this.layout !== 'scroll') return;

    const triggers = this.root.querySelectorAll('[data-lightbox-trigger]');
    if (!triggers.length) return;

    triggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          // Guard against navigation-style clicks bubbling from
          // links / buttons inside the media -- they shouldn't
          // open the lightbox.
          if (event.target.closest('a, button:not([data-lightbox-trigger])')) return;
          this._openLightbox(trigger);
        },
        { signal: this.abortController.signal }
      );

      // Keyboard activation for role="button" + tabindex="0" --
      // mirrors the click handler so Enter / Space open the
      // lightbox identically to a mouse click.
      trigger.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this._openLightbox(trigger);
        },
        { signal: this.abortController.signal }
      );
    });

    // Close button.
    const closeBtn = this.lightboxEl.querySelector('.product-main-gallery__lightbox-close');
    closeBtn?.addEventListener(
      'click',
      () => this._closeLightbox(),
      { signal: this.abortController.signal }
    );

    // ESC-to-close -- document-level so the keydown fires no
    // matter where focus is inside the modal. Guarded by the
    // open-class check so it's a no-op when the modal is
    // closed and other ESC handlers on the page (e.g. menus)
    // aren't accidentally pre-empted.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        if (!this.lightboxEl.classList.contains('is-open')) return;
        this._closeLightbox();
      },
      { signal: this.abortController.signal }
    );
  }

  _openLightbox(trigger) {
    if (!this.lightboxEl) return;

    // Desktop-only gate -- mirrors the CSS `max-width: 991px`
    // display: none on the lightbox. Without this, a touch
    // user who somehow activates the trigger (e.g. via
    // assistive tech) would open an invisible modal.
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    const idx = parseInt(trigger.dataset.lightboxIndex, 10);
    if (Number.isNaN(idx)) return;

    // Remember which element opened the modal so close can
    // restore focus there (a11y -- focus shouldn't get lost
    // on a closing dialog).
    this._lightboxPreviousFocus = document.activeElement;

    this.lightboxEl.classList.add('is-open');
    this.lightboxEl.setAttribute('aria-hidden', 'false');
    // Lock the body so the page beneath the modal doesn't
    // scroll. CSS applies `overflow: hidden` via the class.
    document.body.classList.add('product-lightbox-open');

    this._ensureLightboxSwiper(idx);

    // Move focus into the modal so keyboard users can
    // immediately activate the close button or arrows.
    const closeBtn = this.lightboxEl.querySelector('.product-main-gallery__lightbox-close');
    closeBtn?.focus();
  }

  _closeLightbox() {
    if (!this.lightboxEl) return;
    this.lightboxEl.classList.remove('is-open');
    this.lightboxEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('product-lightbox-open');
    // Restore focus to the element that opened the modal.
    if (this._lightboxPreviousFocus && typeof this._lightboxPreviousFocus.focus === 'function') {
      this._lightboxPreviousFocus.focus();
    }
    this._lightboxPreviousFocus = null;
  }

  /**
   * Lazy-init the lightbox swiper on first open. Subsequent
   * opens just slideTo the new index. Initialising AFTER
   * showModal means slide widths measure correctly (a closed
   * dialog has display:none → 0 width → Swiper would lock).
   */
  _ensureLightboxSwiper(initialIndex) {
    if (!this.lightboxSwiperEl || !Swiper) return;

    if (!this.lightboxSwiper) {
      const slides = this.lightboxSwiperEl.querySelectorAll('.swiper-slide');
      const hasMultiple = slides.length > 1;

      const prevEl = this.lightboxEl.querySelector('.product-main-gallery__lightbox-arrow--prev');
      const nextEl = this.lightboxEl.querySelector('.product-main-gallery__lightbox-arrow--next');
      const bulletsEl = this.lightboxEl.querySelector('.product-main-gallery__lightbox-bullets');

      const options = {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        speed: 400,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        watchOverflow: false,
        observer: true,
        observeParents: true,
        initialSlide: initialIndex,
      };

      if (hasMultiple) {
        if (prevEl && nextEl) {
          options.navigation = { prevEl, nextEl };
        }
        if (bulletsEl) {
          options.pagination = { el: bulletsEl, clickable: true };
        }
      }

      this.lightboxSwiper = new Swiper(this.lightboxSwiperEl, options);
    } else {
      // Already initialised -- just navigate. update() catches
      // any layout drift since the last open.
      this.lightboxSwiper.update();
      this.lightboxSwiper.slideTo(initialIndex, 0);
    }
  }

  destroy() {
    this.abortController.abort();
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy(true, true);
      this.lightboxSwiper = null;
    }
    // Drop the open state + body lock if the modal happened to
    // be open on section unload (customizer section reorder,
    // etc.). abortController has stripped all listeners by
    // this point, so the class removal here is the explicit
    // cleanup path.
    this.lightboxEl?.classList.remove('is-open');
    this.lightboxEl?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('product-lightbox-open');
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductMainGallery(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
